import React, { useEffect, useState } from "react";
import { requestResetCode, confirmReset } from "../utils/passwordResetApi";
import {
  DEFAULT_COOLDOWN,
  MAX_PASSWORD,
  sanitizeCode,
  validateReset,
  secondsLeft,
  failureMessage,
} from "../utils/passwordResetLogic";

const INPUT =
  "w-full px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500";
const LABEL = "block text-sm font-medium text-gray-700 mb-1";
const PRIMARY =
  "w-full bg-purple-600 text-white py-2 rounded-md hover:bg-blue-700 transition duration-200 disabled:opacity-60";
const LINK = {
  backgroundColor: "transparent",
  border: "none",
  color: "#4F46E5",
};
const SENT_MESSAGE =
  "If an account exists for this email, a reset code has been sent.";

const PasswordResetPanel = ({ initialEmail, onDone, onCancel }) => {
  const [step, setStep] = useState("email");
  const [email, setEmail] = useState(initialEmail || "");
  const [code, setCode] = useState("");
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [busy, setBusy] = useState(false);
  const [status, setStatus] = useState({ type: "", text: "" });
  const [deadline, setDeadline] = useState(0);
  const [now, setNow] = useState(Date.now());

  const remaining = secondsLeft(deadline, now);

  useEffect(() => {
    if (!deadline || Date.now() >= deadline) return undefined;
    const id = setInterval(() => {
      setNow(Date.now());
      if (Date.now() >= deadline) clearInterval(id);
    }, 500);
    return () => clearInterval(id);
  }, [deadline]);

  const startCooldown = (seconds) => {
    setNow(Date.now());
    setDeadline(Date.now() + (seconds || DEFAULT_COOLDOWN) * 1000);
  };

  const sendCode = async (advance) => {
    setStatus({ type: "", text: "" });
    setBusy(true);
    const result = await requestResetCode(email.trim());
    setBusy(false);
    if (result.ok) {
      startCooldown(result.retryAfter);
      setStatus({ type: "success", text: SENT_MESSAGE });
      if (advance) setStep("code");
      return;
    }
    if (result.status === 429) startCooldown(result.retryAfter);
    setStatus({ type: "error", text: failureMessage(result, "request") });
  };

  const handleRequest = (e) => {
    e.preventDefault();
    sendCode(true);
  };

  const handleConfirm = async (e) => {
    e.preventDefault();
    const problem = validateReset({ code, password, confirm });
    if (problem) {
      setStatus({ type: "error", text: problem });
      return;
    }
    setStatus({ type: "", text: "" });
    setBusy(true);
    const result = await confirmReset(email.trim(), code, password);
    setBusy(false);
    if (result.ok) {
      onDone(email.trim());
      return;
    }
    setStatus({ type: "error", text: failureMessage(result, "confirm") });
  };

  const changeEmail = () => {
    setStep("email");
    setCode("");
    setPassword("");
    setConfirm("");
    setStatus({ type: "", text: "" });
  };

  return (
    <div>
      <h2 className="text-2xl font-bold text-center text-gray-800 mb-6">
        Reset Password
      </h2>
      <p
        role="status"
        aria-live="polite"
        className={`text-sm mb-4 text-center ${
          status.type === "error" ? "text-red-500" : "text-green-600"
        }`}
      >
        {status.text}
      </p>

      {step === "email" ? (
        <form onSubmit={handleRequest} className="space-y-6">
          <div>
            <label htmlFor="reset-email" className={LABEL}>
              Email
            </label>
            <input
              id="reset-email"
              type="email"
              autoComplete="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
              disabled={busy}
              className={INPUT}
              placeholder="Enter your email"
            />
          </div>
          <button type="submit" className={PRIMARY} disabled={busy}>
            {busy ? "Sending..." : "Send Reset Code"}
          </button>
        </form>
      ) : (
        <form onSubmit={handleConfirm} className="space-y-4" noValidate>
          <p className="text-sm text-gray-600 text-center">
            Enter the 6-digit code sent to {email.trim()} and choose a new
            password.
          </p>
          <div>
            <label htmlFor="reset-code" className={LABEL}>
              6-digit code
            </label>
            <input
              id="reset-code"
              type="text"
              inputMode="numeric"
              autoComplete="one-time-code"
              maxLength={6}
              value={code}
              onChange={(e) => setCode(sanitizeCode(e.target.value))}
              disabled={busy}
              className={`${INPUT} tracking-widest`}
              placeholder="123456"
            />
          </div>
          <div>
            <label htmlFor="reset-password" className={LABEL}>
              New password
            </label>
            <input
              id="reset-password"
              type={showPassword ? "text" : "password"}
              autoComplete="new-password"
              maxLength={MAX_PASSWORD}
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              disabled={busy}
              className={INPUT}
            />
          </div>
          <div>
            <label htmlFor="reset-confirm" className={LABEL}>
              Confirm new password
            </label>
            <input
              id="reset-confirm"
              type={showPassword ? "text" : "password"}
              autoComplete="new-password"
              maxLength={MAX_PASSWORD}
              value={confirm}
              onChange={(e) => setConfirm(e.target.value)}
              disabled={busy}
              className={INPUT}
            />
          </div>
          <label className="flex items-center text-sm text-gray-700">
            <input
              type="checkbox"
              checked={showPassword}
              onChange={(e) => setShowPassword(e.target.checked)}
              className="mr-2"
            />
            Show passwords
          </label>
          <button type="submit" className={PRIMARY} disabled={busy}>
            {busy ? "Updating..." : "Reset Password"}
          </button>
          <div className="flex justify-between text-sm">
            <button
              type="button"
              onClick={() => sendCode(false)}
              disabled={busy || remaining > 0}
              style={{ ...LINK, opacity: busy || remaining > 0 ? 0.5 : 1 }}
            >
              {remaining > 0 ? `Resend code in ${remaining}s` : "Resend code"}
            </button>
            <button
              type="button"
              onClick={changeEmail}
              disabled={busy}
              style={LINK}
            >
              Use a different email
            </button>
          </div>
        </form>
      )}

      <p className="text-sm text-center mt-4">
        <button type="button" onClick={onCancel} disabled={busy} style={LINK}>
          Back to sign in
        </button>
      </p>
    </div>
  );
};

export default PasswordResetPanel;
